import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Public as ZoneIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AnalyticsIcon
} from '@mui/icons-material';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface ZoneSummary {
  dim_school__zone_code: string;
  dim_school__zone_name: string;
  schools_count: number;
  total_students: number;
  total_student_present: number;
  total_teachers: number;
  total_teacher_present: number;
  avg_student_rate: number;
  avg_teacher_rate: number;
  days_reported: number;
}

interface SchoolLevelBreakdown {
  dim_school__zone_code: string;
  dim_school__zone_name: string;
  dim_school__school_level_code: string;
  dim_school__school_level_name: string;
  schools_count: number;
  avg_student_rate: number;
  avg_teacher_rate: number;
  total_students: number;
  total_student_present: number;
}

interface DistrictPerformance {
  dim_school__zone_code: string;
  dim_school__district_code: string;
  dim_school__district_name: string;
  schools_count: number;
  avg_student_rate: number;
  avg_teacher_rate: number;
  total_students: number;
}

interface ZoneDashboardData {
  period: {
    start_date: string;
    end_date: string;
    days_analyzed: number;
  };
  zone_summary: ZoneSummary[];
  school_level_breakdown: SchoolLevelBreakdown[];
  district_performance: DistrictPerformance[];
}

const ZoneDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<ZoneDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [dateRange, setDateRange] = useState<number>(30);

  useEffect(() => {
    fetchZoneDashboard();
  }, [selectedZone, dateRange]);

  const fetchZoneDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');

      const response = await attendanceAPI.getZoneDashboard({
        zone: selectedZone || undefined,
        days_back: dateRange,
      });

      if (response.data.status === 'success') {
        setDashboardData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load zone dashboard data');
      }
    } catch (err: any) {
      console.error('Zone dashboard error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load zone dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 95) return 'success';
    if (rate >= 85) return 'warning';
    return 'error';
  };

  // Prepare zone comparison chart
  const getZoneComparisonChart = () => {
    if (!dashboardData?.zone_summary || dashboardData.zone_summary.length === 0) return null;

    const zones = dashboardData.zone_summary;

    return {
      labels: zones.map(z => z.dim_school__zone_name),
      datasets: [
        {
          label: 'Student Attendance %',
          data: zones.map(z => z.avg_student_rate),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
        {
          label: 'Teacher Attendance %',
          data: zones.map(z => z.avg_teacher_rate),
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  // Prepare school level pie chart
  const getSchoolLevelChart = () => {
    if (!dashboardData?.school_level_breakdown || dashboardData.school_level_breakdown.length === 0) return null;

    const levels = dashboardData.school_level_breakdown;
    const levelCounts = levels.reduce((acc, level) => {
      acc[level.dim_school__school_level_name] = (acc[level.dim_school__school_level_name] || 0) + level.schools_count;
      return acc;
    }, {} as Record<string, number>);

    return {
      labels: Object.keys(levelCounts),
      datasets: [
        {
          data: Object.values(levelCounts),
          backgroundColor: [
            'rgba(255, 99, 132, 0.6)',
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 205, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No zone dashboard data available
      </Alert>
    );
  }

  const zoneChart = getZoneComparisonChart();
  const levelChart = getSchoolLevelChart();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        <ZoneIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        Zone Analytics Dashboard
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Comprehensive attendance analytics for Pemba and Unguja zones
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Zone</InputLabel>
                <Select
                  value={selectedZone}
                  label="Zone"
                  onChange={(e) => setSelectedZone(e.target.value)}
                >
                  <MenuItem value="">All Zones</MenuItem>
                  <MenuItem value="PEMBA">Pemba</MenuItem>
                  <MenuItem value="UNGUJA">Unguja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select
                  value={dateRange}
                  label="Period"
                  onChange={(e) => setDateRange(Number(e.target.value))}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={fetchZoneDashboard}
                fullWidth
                startIcon={<AnalyticsIcon />}
              >
                Refresh Analytics
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Period Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Analysis Period
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">Period</Typography>
              <Typography variant="h6">
                {dashboardData.period.start_date} to {dashboardData.period.end_date}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">Days Analyzed</Typography>
              <Typography variant="h6">{dashboardData.period.days_analyzed}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">Total Zones</Typography>
              <Typography variant="h6">{dashboardData.zone_summary.length}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Zone Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {dashboardData.zone_summary.map((zone, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  <ZoneIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {zone.dim_school__zone_name} Zone
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Schools</Typography>
                    <Typography variant="h5">{zone.schools_count}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Days Reported</Typography>
                    <Typography variant="h5">{zone.days_reported}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Total Students</Typography>
                    <Typography variant="h5">{zone.total_students?.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Total Teachers</Typography>
                    <Typography variant="h5">{zone.total_teachers?.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Student Attendance</Typography>
                    <Chip
                      label={`${zone.avg_student_rate.toFixed(1)}%`}
                      color={getPerformanceColor(zone.avg_student_rate)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Teacher Attendance</Typography>
                    <Chip
                      label={`${zone.avg_teacher_rate.toFixed(1)}%`}
                      color={getPerformanceColor(zone.avg_teacher_rate)}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {zoneChart && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Zone Attendance Comparison
                </Typography>
                <Bar data={zoneChart} options={chartOptions} />
              </CardContent>
            </Card>
          </Grid>
        )}

        {levelChart && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Schools by Level
                </Typography>
                <Pie data={levelChart} />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* School Level Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            School Level Performance Breakdown
          </Typography>

          {dashboardData.zone_summary.map((zone) => (
            <Accordion key={zone.dim_school__zone_code}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{zone.dim_school__zone_name} Zone</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School Level</TableCell>
                        <TableCell align="right">Schools Count</TableCell>
                        <TableCell align="right">Total Students</TableCell>
                        <TableCell align="right">Student Rate</TableCell>
                        <TableCell align="right">Teacher Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardData.school_level_breakdown
                        .filter(level => level.dim_school__zone_code === zone.dim_school__zone_code)
                        .map((level, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                                {level.dim_school__school_level_name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{level.schools_count}</TableCell>
                            <TableCell align="right">{level.total_students?.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${level.avg_student_rate.toFixed(1)}%`}
                                color={getPerformanceColor(level.avg_student_rate)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${level.avg_teacher_rate.toFixed(1)}%`}
                                color={getPerformanceColor(level.avg_teacher_rate)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      {/* District Performance */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            District Performance Rankings
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>District</TableCell>
                  <TableCell>Zone</TableCell>
                  <TableCell align="right">Schools</TableCell>
                  <TableCell align="right">Total Students</TableCell>
                  <TableCell align="right">Student Rate</TableCell>
                  <TableCell align="right">Teacher Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.district_performance
                  .sort((a, b) => b.avg_student_rate - a.avg_student_rate)
                  .map((district, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{district.dim_school__district_name}</TableCell>
                      <TableCell>{district.dim_school__zone_code}</TableCell>
                      <TableCell align="right">{district.schools_count}</TableCell>
                      <TableCell align="right">{district.total_students?.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${district.avg_student_rate.toFixed(1)}%`}
                          color={getPerformanceColor(district.avg_student_rate)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${district.avg_teacher_rate.toFixed(1)}%`}
                          color={getPerformanceColor(district.avg_teacher_rate)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ZoneDashboard;